import React, {Component} from 'react';
import {withTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';
import '../../../sass/elements/_ga4-filters-editor.scss';

export const FIELD_SOURCE = 'SOURCE';
export const FIELD_MEDIUM = 'MEDIUM';
export const FIELD_CAMPAIGN = 'CAMPAIGN';
export const FIELD_LANDING_PAGE = 'LANDING_PAGE';
export const FIELD_PRODUCT_NAME = 'PRODUCT_NAME';
export const FIELD_PRODUCT_ID = 'PRODUCT_ID';

export const OPERATOR_EQUALS = 'EQUALS';
export const OPERATOR_CONTAINS = 'CONTAINS';
export const OPERATOR_IN_LIST = 'IN_LIST';
export const OPERATOR_REGEXP_PARTIAL_MATCH = 'REGEXP_PARTIAL_MATCH';

const DEFAULT_AVAILABLE_FIELDS = [
    FIELD_SOURCE, FIELD_MEDIUM, FIELD_CAMPAIGN, FIELD_LANDING_PAGE,
    FIELD_PRODUCT_NAME, FIELD_PRODUCT_ID,
];

const ALL_OPERATORS = [
    OPERATOR_CONTAINS, OPERATOR_EQUALS, OPERATOR_IN_LIST, OPERATOR_REGEXP_PARTIAL_MATCH,
];

const MULTI_VALUE_OPERATORS = [OPERATOR_CONTAINS, OPERATOR_IN_LIST];

const FIELD_TO_ACCENT_CLASS = {
    [FIELD_SOURCE]: 'field-source',
    [FIELD_MEDIUM]: 'field-medium',
    [FIELD_CAMPAIGN]: 'field-campaign',
    [FIELD_LANDING_PAGE]: 'field-landing_page',
    [FIELD_PRODUCT_NAME]: 'field-product_name',
    [FIELD_PRODUCT_ID]: 'field-product_id',
};

class GA4FiltersEditorClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // texto "en progreso" dentro del chip-input, por cada fila
            editingTextByIndex: {},
        };
        this.handleFieldChange = this.handleFieldChange.bind(this);
        this.handleOperatorChange = this.handleOperatorChange.bind(this);
        this.handleSingleValueChange = this.handleSingleValueChange.bind(this);
        this.handleAddFilter = this.handleAddFilter.bind(this);
        this.handleRemoveFilter = this.handleRemoveFilter.bind(this);
    }

    availableFields() {
        return this.props.availableFields && this.props.availableFields.length > 0
            ? this.props.availableFields
            : DEFAULT_AVAILABLE_FIELDS;
    }

    currentFilters() {
        return Array.isArray(this.props.filters) ? this.props.filters : [];
    }

    emitChange(newFilters) {
        if (this.props.onChange) {
            this.props.onChange(newFilters);
        }
    }

    handleFieldChange(index, event) {
        const newFilters = [...this.currentFilters()];
        newFilters[index] = {...newFilters[index], field: event.target.value};
        this.emitChange(newFilters);
    }

    handleOperatorChange(index, event) {
        const newOperator = event.target.value;
        const newFilters = [...this.currentFilters()];
        const existing = newFilters[index] || {};
        let newValues = Array.isArray(existing.values) ? [...existing.values] : [];

        // Si cambia a operador de valor único (EQUALS/REGEXP), conservar solo el primero
        if (!MULTI_VALUE_OPERATORS.includes(newOperator) && newValues.length > 1) {
            newValues = newValues.slice(0, 1);
        }
        newFilters[index] = {...existing, operator: newOperator, values: newValues};

        // Limpiar buffer de edición al cambiar operador
        this._clearEditingText(index);
        this.emitChange(newFilters);
    }

    handleSingleValueChange(index, event) {
        const text = event.target.value;
        const newFilters = [...this.currentFilters()];
        const existing = newFilters[index] || {};
        newFilters[index] = {...existing, values: text === '' ? [] : [text]};
        this.emitChange(newFilters);
    }

    handleAddFilter() {
        const fields = this.availableFields();
        const newFilters = [...this.currentFilters(), {
            field: fields[0],
            operator: OPERATOR_CONTAINS,
            values: [],
        }];
        this.emitChange(newFilters);
    }

    handleRemoveFilter(index) {
        const newFilters = this.currentFilters().filter((_, i) => i !== index);
        this._clearEditingText(index);
        this.emitChange(newFilters);
    }

    _clearEditingText(index) {
        this.setState(prev => {
            const next = {...prev.editingTextByIndex};
            delete next[index];
            return {editingTextByIndex: next};
        });
    }

    _setEditingText(index, text) {
        this.setState(prev => ({
            editingTextByIndex: {...prev.editingTextByIndex, [index]: text},
        }));
    }

    _commitEditingChip(index) {
        const text = (this.state.editingTextByIndex[index] || '').trim();
        if (!text) return;

        const newFilters = [...this.currentFilters()];
        const existing = newFilters[index] || {};
        const currentValues = Array.isArray(existing.values) ? existing.values : [];

        // Evitar duplicados
        if (currentValues.includes(text)) {
            this._clearEditingText(index);
            return;
        }

        newFilters[index] = {...existing, values: [...currentValues, text]};
        this._clearEditingText(index);
        this.emitChange(newFilters);
    }

    _removeChipAt(index, chipIndex) {
        const newFilters = [...this.currentFilters()];
        const existing = newFilters[index] || {};
        const currentValues = Array.isArray(existing.values) ? existing.values : [];
        newFilters[index] = {...existing, values: currentValues.filter((_, i) => i !== chipIndex)};
        this.emitChange(newFilters);
    }

    _handleChipInputKeyDown(index, event) {
        const text = this.state.editingTextByIndex[index] || '';

        if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            this._commitEditingChip(index);
            return;
        }

        if (event.key === 'Backspace' && text === '') {
            const currentValues = this.currentFilters()[index]?.values || [];
            if (currentValues.length > 0) {
                event.preventDefault();
                this._removeChipAt(index, currentValues.length - 1);
            }
        }
    }

    _handleChipInputChange(index, event) {
        const text = event.target.value;
        // Si el usuario pega "foo, bar, baz", commitear todos
        if (text.includes(',')) {
            const parts = text.split(',').map(p => p.trim()).filter(Boolean);
            if (parts.length > 0) {
                const newFilters = [...this.currentFilters()];
                const existing = newFilters[index] || {};
                const currentValues = Array.isArray(existing.values) ? existing.values : [];
                const added = parts.filter(p => !currentValues.includes(p));
                newFilters[index] = {...existing, values: [...currentValues, ...added]};
                this._clearEditingText(index);
                this.emitChange(newFilters);
                return;
            }
        }
        this._setEditingText(index, text);
    }

    _handleChipInputBlur(index) {
        this._commitEditingChip(index);
    }

    render() {
        const {t} = this.props;
        const filters = this.currentFilters();
        const fields = this.availableFields();
        const hasFilters = filters.length > 0;

        const rows = [];
        filters.forEach((f, index) => {
            if (index > 0) {
                rows.push(
                    <div key={`and-${index}`} className="ga4-filters-editor-and-separator">
                        <span>{t('ga4_filters.and_separator')}</span>
                    </div>
                );
            }
            rows.push(this._renderRow(f, index, fields, t));
        });

        return (
            <div className="ga4-filters-editor">
                <div className="ga4-filters-editor-header">
                    <FaIcon iconName="filter" className="ga4-filters-editor-header-icon" />
                    <span>{t('ga4_filters.section_title')}</span>
                </div>

                <div className="ga4-filters-editor-note">
                    {t('ga4_filters.granularity_note')}
                </div>

                {!hasFilters && (
                    <div className="ga4-filters-editor-empty">
                        {t('ga4_filters.no_filters')}
                    </div>
                )}

                {rows}

                <div className="ga4-filters-editor-actions">
                    <button
                        type="button"
                        className="ga4-filters-editor-add"
                        onClick={this.handleAddFilter}
                    >
                        <span className="ga4-filters-editor-add-icon">+</span>
                        {t('ga4_filters.add_filter')}
                    </button>
                    {hasFilters && (
                        <span className="ga4-filters-editor-count">
                            {t('ga4_filters.filter_count', {count: filters.length})}
                        </span>
                    )}
                </div>
            </div>
        );
    }

    _renderRow(f, index, fields, t) {
        const fieldId = f.field || fields[0];
        const accentClass = FIELD_TO_ACCENT_CLASS[fieldId] || '';
        const operator = f.operator || OPERATOR_CONTAINS;

        return (
            <div
                key={index}
                className={`ga4-filters-editor-row ${accentClass}`}
            >
                <select
                    className="ga4-filters-editor-field"
                    value={fieldId}
                    onChange={(e) => this.handleFieldChange(index, e)}
                    aria-label={t('ga4_filters.field_label')}
                >
                    {fields.map(fId => (
                        <option key={fId} value={fId}>
                            {t(`ga4_filters.field_${fId.toLowerCase()}`)}
                        </option>
                    ))}
                </select>

                <select
                    className="ga4-filters-editor-operator"
                    value={operator}
                    onChange={(e) => this.handleOperatorChange(index, e)}
                    aria-label={t('ga4_filters.operator_label')}
                >
                    {ALL_OPERATORS.map(op => (
                        <option key={op} value={op}>
                            {t(`ga4_filters.operator_${op.toLowerCase()}`)}
                        </option>
                    ))}
                </select>

                {this._renderValuesInput(f, index, fieldId, operator, t)}

                <button
                    type="button"
                    className="ga4-filters-editor-remove"
                    aria-label={t('ga4_filters.remove_filter')}
                    onClick={() => this.handleRemoveFilter(index)}
                >
                    ×
                </button>
            </div>
        );
    }

    _renderValuesInput(filter, index, fieldId, operator, t) {
        const values = Array.isArray(filter.values) ? filter.values : [];
        const isMultiValue = MULTI_VALUE_OPERATORS.includes(operator);

        if (isMultiValue) {
            const editingText = this.state.editingTextByIndex[index] || '';
            const showHint = values.length === 0 && editingText === '';
            return (
                <div className="ga4-filters-editor-chips" onClick={(e) => this._focusChipInput(e)}>
                    {values.map((value, chipIndex) => (
                        <span key={`${value}-${chipIndex}`} className="ga4-filters-editor-chip">
                            <span className="ga4-filters-editor-chip-text">{value}</span>
                            <button
                                type="button"
                                className="ga4-filters-editor-chip-remove"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this._removeChipAt(index, chipIndex);
                                }}
                                aria-label={t('ga4_filters.remove_value', {value})}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    <input
                        type="text"
                        className="ga4-filters-editor-chip-input"
                        value={editingText}
                        placeholder={values.length === 0 ? this._placeholderFor(fieldId, operator, t) : ''}
                        onChange={(e) => this._handleChipInputChange(index, e)}
                        onKeyDown={(e) => this._handleChipInputKeyDown(index, e)}
                        onBlur={() => this._handleChipInputBlur(index)}
                        aria-label={t('ga4_filters.values_label')}
                    />
                    {showHint && (
                        <span className="ga4-filters-editor-chip-hint">
                            {t('ga4_filters.multi_value_hint')}
                        </span>
                    )}
                </div>
            );
        }

        const isRegex = operator === OPERATOR_REGEXP_PARTIAL_MATCH;
        const singleClass = [
            'ga4-filters-editor-values',
            isRegex ? 'is-regex' : '',
        ].filter(Boolean).join(' ');

        return (
            <input
                type="text"
                className={singleClass}
                value={values[0] || ''}
                placeholder={this._placeholderFor(fieldId, operator, t)}
                onChange={(e) => this.handleSingleValueChange(index, e)}
                aria-label={t('ga4_filters.values_label')}
            />
        );
    }

    _focusChipInput(event) {
        // Clic en cualquier parte del contenedor enfoca el input
        const input = event.currentTarget.querySelector('.ga4-filters-editor-chip-input');
        if (input) input.focus();
    }

    _placeholderFor(fieldId, operator, t) {
        const specific = `ga4_filters.values_placeholder_${operator.toLowerCase()}_${fieldId.toLowerCase()}`;
        const byOperator = `ga4_filters.values_placeholder_${operator.toLowerCase()}`;
        const fallback = t('ga4_filters.values_placeholder');

        const specificTranslation = t(specific, {defaultValue: ''});
        if (specificTranslation) {
            return specificTranslation;
        }
        const byOperatorTranslation = t(byOperator, {defaultValue: ''});
        if (byOperatorTranslation) {
            return byOperatorTranslation;
        }
        return fallback;
    }
}

export const GA4FiltersEditor = withTranslation()(GA4FiltersEditorClass);
export default GA4FiltersEditor;
